import { useCallback, useMemo, useState } from 'react';

import {
  ListReservedValues,
  ListValue,
  useItemsSearch,
  useKeyboardNavigation,
} from '@xemplo/stateful-list-core';

import { SingleSelectContextType } from '../../utils/types/stateful-list.types';

import { SingleSelectInputProps } from './single-select.types';

export function useSingleState({
  allowDeselect = false,
  items,
  useSearch = false,
  defaultSelected,
  onChange,
  autoScrollToSelected = true,
}: SingleSelectInputProps) {
  const [selectedItem, setSelected] = useState<ListValue | undefined>(defaultSelected);
  const { renderItems, valueItems, valueItemMap, isNoResult, search, setSearch } =
    useItemsSearch({ items });

  const toggleItem = useCallback(
    (value: ListValue) => {
      // Comparing to the value to avoid adding another value into the dependency array
      if (value === ListReservedValues.noResult) return; // Ignore no result item

      const item = valueItemMap.get(value);
      if (!item) {
        console.warn(`Item with value "${value}" not found in the list.`);
        return; // If item not found, do nothing
      }

      setSelected((prev) => {
        const isDeselect = allowDeselect && prev === value;
        const next = isDeselect ? undefined : value;

        next && item.itemCallback?.(next); // Call the item's callback if it exists
        onChange?.(next); // Notify parent of the new selection
        return next; // Select the new item
      });
    },
    [allowDeselect, onChange, valueItemMap],
  );

  // Patch valueItems to include the itemCallback for selection
  // This allows each item to call handleSetSelected when clicked
  // or selected via keyboard navigation
  const patchedValueItems = useMemo(
    () => valueItems.map((item) => ({ ...item, itemCallback: toggleItem })),
    [toggleItem, valueItems],
  );

  const isSelected = useCallback(
    (value: ListValue) => (selectedItem ? selectedItem === value : false),
    [selectedItem],
  );

  const { activeDescendantId, containerRef, onKeyDown } = useKeyboardNavigation({
    id: renderItems.id,
    items: patchedValueItems,
    useSearch,
    useSelectAll: false, // Single select does not use select all
  });

  return useMemo(
    () =>
      ({
        activeDescendantId,
        autoScrollToSelected,
        containerRef,
        isNoResult,
        isSelected,
        items,
        onKeyDown,
        renderItems,
        search,
        selectedItem,
        setSearch,
        toggleItem,
        useSearch,
      }) satisfies SingleSelectContextType,
    [
      activeDescendantId,
      autoScrollToSelected,
      containerRef,
      isNoResult,
      isSelected,
      items,
      onKeyDown,
      renderItems,
      search,
      selectedItem,
      setSearch,
      toggleItem,
      useSearch,
    ],
  );
}
